/**
 * Skills Lab — staff-only workspace for iterating on Claude Skills that
 * encode the outreach (and eventually reply / content) prompt rules.
 *
 * Workflow Eli/TJ runs:
 *   1. Download SKILL.md + LEAD_TEST_TEMPLATE.csv from this page.
 *   2. Open Claude desktop, upload both files.
 *   3. Claude generates messages for the 10 sample leads.
 *   4. Give feedback in plain English; Claude refines the SKILL.md.
 *   5. Iterate until the bar is right; send refined SKILL.md to engineering.
 *
 * Bundle privacy:
 *   The skill files used to live in /public/skills/dealcloser-outreach and
 *   were directly URL-accessible to anyone. Per KNWN4 directive the bundle is
 *   now imported via Vite's ?raw syntax from src/features/admin/skills-bundle
 *   — the content ships INSIDE the dashboard JS chunk, not as standalone
 *   URLs. Downloads are served from in-memory Blob URLs created on click.
 *   Casual visitors hitting app.dealcloserautopilot.com no longer see a
 *   /skills/* tree; only authenticated users who load the dashboard receive
 *   the bundle (and even then it's buried in the minified JS).
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Sparkles,
  Download,
  FileText,
  Table as TableIcon,
  BookOpen,
  Loader2,
  ExternalLink,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'

// Vite ?raw imports bundle the file contents as strings at build time. No
// network fetch, no public URL — the strings live inside the JS chunk that
// only super-admin users load.
import SKILL_CONTENT from './skills-bundle/SKILL.md?raw'
import LEAD_CSV from './skills-bundle/LEAD_TEST_TEMPLATE.csv?raw'
import FOLLOWUP_CSV from './skills-bundle/FOLLOWUP_TEST_TEMPLATE.csv?raw'
import REPLY_CSV from './skills-bundle/REPLY_TEST_TEMPLATE.csv?raw'
import FAQ_CSV from './skills-bundle/FAQ_TEST_TEMPLATE.csv?raw'
import README_CONTENT from './skills-bundle/README.md?raw'

type SkillMeta = {
  readonly name: string
  readonly description: string
  readonly bytes: number
  readonly lines: number
  readonly strategies: number
  readonly templates: number
}

function parseFrontmatter(md: string): { name: string; description: string } {
  const match = md.match(/^---\n([\s\S]*?)\n---/)
  if (!match) return { name: 'unknown', description: '' }
  const body = match[1] ?? ''
  const name = (body.match(/^name:\s*(.+)$/m)?.[1] ?? '').trim()
  const desc = (body.match(/^description:\s*(.+)$/m)?.[1] ?? '').trim()
  return { name, description: desc }
}

function summarize(md: string): SkillMeta {
  const fm = parseFrontmatter(md)
  // Count the 5 Styles (Principle 4) and the worked example fenced blocks.
  // Falls back to legacy heading patterns for older SKILL.md versions so
  // the lab still renders something sensible if the file format drifts.
  const styles = (md.match(/^### Style \d/gm) ?? []).length
  const strategies = styles > 0 ? styles : (md.match(/^## 3\.\d /gm) ?? []).length
  const templates = (md.match(/^```\s*$/gm) ?? []).length / 2 // paired fences
    || (md.match(/^\d+\.\s+`hey \{first_name\}/gm) ?? []).length
  return {
    name: fm.name,
    description: fm.description,
    bytes: new Blob([md]).size,
    lines: md.split('\n').length,
    strategies,
    templates: Math.floor(templates),
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

/**
 * Trigger a download from in-memory string content. Creates a Blob URL, drives
 * a hidden anchor's click, then revokes the URL. Keeps the bundle content out
 * of public storage — only what the operator explicitly downloads ever leaves
 * memory.
 */
function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Revoke on the next tick so Safari/Firefox finish the download first.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function DownloadButton({
  content,
  mime,
  label,
  filename,
  icon: Icon,
  hint,
}: {
  readonly content: string
  readonly mime: string
  readonly label: string
  readonly filename: string
  readonly icon: typeof Download
  readonly hint: string
}) {
  return (
    <button
      type="button"
      onClick={() => downloadBlob(content, filename, mime)}
      className="group flex flex-col gap-2 rounded-lg border border-[rgba(255,255,255,0.06)] bg-[rgba(16,185,129,0.04)] p-4 text-left transition-all hover:border-[rgba(16,185,129,0.3)] hover:bg-[rgba(16,185,129,0.08)]"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#34D399]" />
          <span className="text-sm font-medium text-[var(--color-silver-bright)]">
            {label}
          </span>
        </div>
        <Download className="h-3.5 w-3.5 text-[var(--color-silver)] transition-colors group-hover:text-[#34D399]" />
      </div>
      <span className="text-xs text-[var(--color-silver)]">{hint}</span>
      <span className="font-mono text-[10px] text-[var(--color-silver)]/70">{filename}</span>
    </button>
  )
}

function StepCard({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="flex gap-3 rounded-md border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] p-3">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgba(16,185,129,0.15)] text-xs font-bold text-[#34D399]">
        {n}
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium text-[var(--color-silver-bright)]">{title}</p>
        <p className="text-xs leading-relaxed text-[var(--color-silver)]">{body}</p>
      </div>
    </div>
  )
}

export function SkillsLabPage() {
  // Content comes from build-time imports — no fetch, no public URL. Keep the
  // useState/useEffect shape so the loading branch still renders harmlessly on
  // the unlikely chance Vite returns an empty string (e.g. if a future
  // contributor breaks the ?raw import).
  const [skillContent, setSkillContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    try {
      setSkillContent(SKILL_CONTENT)
    } catch (e) {
      setError(String(e))
    }
  }, [])

  const meta = useMemo<SkillMeta | null>(
    () => (skillContent ? summarize(skillContent) : null),
    [skillContent],
  )

  return (
    <div className="space-y-5">
      {/* Intro */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-[#34D399]" />
            Skills Lab
            <Badge className="border-[rgba(16,185,129,0.25)] bg-[rgba(16,185,129,0.12)] text-[10px] text-[#34D399]">
              v1
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-[var(--color-silver)]">
          <p>
            A Claude{' '}
            <span className="text-[var(--color-silver-bright)]">Skill</span> is a
            saved instruction packet — you hand it to Claude once, then say
            "use this skill on these leads" and Claude follows it exactly.
            Skills are how Eli + TJ refine the outreach prompts conversationally
            without waiting on engineering.
          </p>
          <p>
            Download the bundle below, open Claude desktop, upload the files,
            and start iterating. When the output is at the bar you want, send
            the refined SKILL.md back to engineering and we ship it into the
            production prompt pipeline.
          </p>
        </CardContent>
      </Card>

      {/* Workflow */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Workflow — four rounds in one chat</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2">
          <StepCard
            n={1}
            title="Download the bundle"
            body="Grab SKILL.md and all four CSVs (first DMs, follow-ups, replies, FAQ-grounded replies). They share the same 10 personas where possible so you can compare Style choice, cadence, reply handling, and FAQ grounding on overlapping leads."
          />
          <StepCard
            n={2}
            title="Upload SKILL.md first, then Round A — first DMs"
            body="Open a Claude chat, drag in SKILL.md, then LEAD_TEST_TEMPLATE.csv. Ask Claude to fill in `generated_message` for all 10 rows. Each row already has the recommended Style + seniority track + post offer-relevance — Claude should follow them unless it genuinely deviates."
          />
          <StepCard
            n={3}
            title="Round B — follow-ups"
            body="In the same chat, drag in FOLLOWUP_TEST_TEMPLATE.csv. Each row has a prior_first_dm pre-filled plus the target step_type (FU1/FU2/FU3/breakup) and days_since_first_dm. Ask Claude to generate each follow-up respecting the senior vs mid/junior cadence fork."
          />
          <StepCard
            n={4}
            title="Round C — reply simulation"
            body="Drag in REPLY_TEST_TEMPLATE.csv. Each row has a prior_outbound + a simulated lead reply across all 9 inbound categories. Ask Claude to classify the reply (verify against expected_intent_category) AND generate the next outbound following Section 10's response map."
          />
          <StepCard
            n={5}
            title="Round D — FAQ-grounded replies"
            body="Drag in FAQ_TEST_TEMPLATE.csv. Each row has a prior_outbound, a `faq_entries` JSON column (the Q&A pairs the user saved in Outreach Settings), and a lead reply that asks one or more FAQ questions. Tell Claude to treat the faq_entries as an injected FAQ block per SKILL.md Section 11.1 — ground the reply in the matching answer, paraphrase, preserve every specific (numbers, named features, URLs), and never invent details."
          />
          <StepCard
            n={6}
            title="Iterate in plain English"
            body={
              'For any output that\'s off, tell Claude what\'s wrong (e.g. "F2 invented a SOC 1 cert that\'s not in the FAQ, harden Section 11.1 against fabrication" or "FU1 on row 2 leaked a pitch, that\'s a Section 4 violation"). Claude edits the SKILL.md rule, not just the message.'
            }
          />
          <StepCard
            n={7}
            title="Send the refined SKILL.md + filled CSVs back"
            body="When all four rounds clear your bar, ask Claude to show the final SKILL.md. Paste it + the four filled CSVs into a doc for engineering. We'll merge into outreach.ts, templates.ts, the reply-handler classifier, and the FAQ injection path."
          />
        </CardContent>
      </Card>

      {/* Download bundle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">DealCloser Outreach skill bundle</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <DownloadButton
              content={SKILL_CONTENT}
              mime="text/markdown"
              label="SKILL.md"
              filename="SKILL.md"
              icon={FileText}
              hint="12 writing principles, the 5-Style library, 7 pre-ship checks, step-type structure, sender config, worked example. This is the binder Claude follows."
            />
            <DownloadButton
              content={LEAD_CSV}
              mime="text/csv"
              label="Round A — first DMs"
              filename="LEAD_TEST_TEMPLATE.csv"
              icon={TableIcon}
              hint="10 leads pre-classified across the 5 Styles + seniority tracks + post offer-relevance. Use this to test first_dm generation."
            />
            <DownloadButton
              content={FOLLOWUP_CSV}
              mime="text/csv"
              label="Round B — follow-ups"
              filename="FOLLOWUP_TEST_TEMPLATE.csv"
              icon={TableIcon}
              hint="Same 10 leads with a prior_first_dm + step_type (FU1/FU2/FU3/breakup) + days_since. Test the cadence fork between senior and mid/junior tracks."
            />
            <DownloadButton
              content={REPLY_CSV}
              mime="text/csv"
              label="Round C — reply simulation"
              filename="REPLY_TEST_TEMPLATE.csv"
              icon={TableIcon}
              hint="Same 10 leads with a prior_outbound + simulated lead reply across all 9 inbound categories. Test both the classifier and the next-outbound generation."
            />
            <DownloadButton
              content={FAQ_CSV}
              mime="text/csv"
              label="Round D — FAQ-grounded replies"
              filename="FAQ_TEST_TEMPLATE.csv"
              icon={TableIcon}
              hint="10 scenarios with a prior_outbound + faq_entries JSON + lead reply asking one or more FAQ questions. Test that the AI grounds replies in the saved answer (paraphrased, specifics preserved, no fabrication)."
            />
            <DownloadButton
              content={README_CONTENT}
              mime="text/markdown"
              label="README"
              filename="README.md"
              icon={BookOpen}
              hint="Step-by-step usage guide. Read this first if you're new to Skills."
            />
          </div>

          {meta && (
            <div className="flex flex-wrap gap-3 border-t border-[rgba(255,255,255,0.06)] pt-3 text-xs text-[var(--color-silver)]">
              <span>
                Skill name:{' '}
                <span className="font-mono text-[var(--color-silver-bright)]">{meta.name}</span>
              </span>
              <span>•</span>
              <span>
                Size:{' '}
                <span className="text-[var(--color-silver-bright)]">{formatBytes(meta.bytes)}</span>
              </span>
              <span>•</span>
              <span>
                {meta.lines.toLocaleString()} lines
              </span>
              <span>•</span>
              <span>
                {meta.strategies} styles, {meta.templates} worked-example messages
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* External link */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Resources</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <a
            href="https://platform.claude.com/docs/en/build-with-claude/skills-guide"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-[#34D399] hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Claude Skills documentation (platform.claude.com)
          </a>
          <a
            href="https://claude.ai"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-[#34D399] hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open claude.ai (where you'll iterate on the skill)
          </a>
        </CardContent>
      </Card>

      {/* SKILL.md preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-sm">
            <span>Current SKILL.md preview</span>
            {meta && (
              <Badge className="bg-[rgba(255,255,255,0.05)] text-[10px] text-[var(--color-silver-bright)]">
                read-only
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
              Failed to load skill: {error}
            </div>
          )}
          {!skillContent && !error && (
            <div className="flex items-center gap-2 py-8 text-sm text-[var(--color-silver)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading skill content…
            </div>
          )}
          {skillContent && (
            <ScrollArea className="h-[480px] rounded-md border border-[rgba(255,255,255,0.06)] bg-[rgba(0,0,0,0.25)]">
              <pre className="whitespace-pre-wrap p-4 font-mono text-[11px] leading-relaxed text-[var(--color-silver-bright)]">
                {skillContent}
              </pre>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
